import {useMemo, useRef} from 'react'
import {type StyleProp, StyleSheet, View, type ViewStyle} from 'react-native'

import {type FlashBookDirection, slideDirection} from '#/lib/flashbookDirection'
import {SlidePlayerRenderer} from '#/components/capsules/play/SlidePlayerRenderer'
import {type SlideAnswerResult} from '#/components/capsules/play/types'
import {FlashBookContentBlock} from '#/components/flashbooks/FlashBookContentBlock'
import {
  FlashBookPlayThemeProvider,
  getFlashBookPlayPalette,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {useFlashBookInkSound} from '#/components/flashbooks/useFlashBookInkSound'
import {designInHand, type FlashBookDesign} from '#/themes-flashbook/design'
import {type Slide, SLIDE_TYPE_META, slideBlockKey} from '#/types/capsule'
import {type FlashBookThemeTokens} from '#/types/flashbook'

type Props = {
  block: Slide
  tokens: FlashBookThemeTokens
  /** The book's design language — decides how this block sits on the page. */
  design: FlashBookDesign
  onResult: (result: SlideAnswerResult) => void
  isPreview?: boolean
  /**
   * The page's own direction, for a block that has no writing of its own to
   * judge by — a blank image, a figure with no caption. Passed in rather than
   * read from context because this component *renders* the provider its
   * children see, so from its own body the page is invisible.
   */
  pageDirection?: FlashBookDirection
}

/**
 * Renders a single FlashBook block as part of a page.
 *
 * The block does *not* invent its own look: the theme's `block.mode` decides.
 * `open` themes (storybook, magazine) leave questions flowing directly on the
 * paper so a page reads as a page; `panel` themes (academy, space) give each
 * block a bounded surface because a focused study interface wants them; `ruled`
 * sets an exercise above a rule the way a workbook does. Nothing here branches
 * on theme id — only on the declarative design.
 *
 * Content blocks additionally get the reading-audio treatment (recorded audio
 * or TTS, with background music ducking automatically). Everything else reuses
 * the shared capsule widgets.
 */
export function FlashBookBlockPlayer({
  block,
  tokens,
  design,
  onResult,
  isPreview = false,
  pageDirection = 'ltr',
}: Props) {
  const lastReported = useRef(false)
  const accent = tokens.accent
  const palette = useMemo(() => getFlashBookPlayPalette(tokens), [tokens])

  const isContent = block.type === 'content'
  const meta = SLIDE_TYPE_META[block.type]
  /**
   * Direction is decided by the block's own writing, and by the page only when
   * the block has none. That is what lets one page of a workbook hold an Arabic
   * exercise and an English quotation without either being laid out the other's
   * way — the smallest unit a creator writes is the unit that gets a direction.
   */
  const direction = useMemo(
    () => slideDirection(block) ?? pageDirection,
    [block, pageDirection],
  )

  /**
   * The design in the hand this block is written in.
   *
   * On a device a face is one family with no per-glyph fallback, so an Arabic
   * block needs the Arabic hand named on its roles — and a block is the smallest
   * thing a creator writes, so it is also the right granularity: an English
   * quotation on an Arabic page is written in Latin and keeps the Latin hand.
   *
   * Resolved here as well as in the provider below because this component's
   * *content* path renders no provider: a reading block prints straight onto the
   * page, so nothing between it and the theme would otherwise re-resolve its
   * title and its prose.
   */
  const blockDesign = useMemo(
    () => designInHand(design, direction),
    [design, direction],
  )

  /*
   * The book's verdict sound.
   *
   * `inBook` and the design are both passed rather than read from context: this
   * component is the one that *renders* the play provider, so its own body sits
   * outside it — the widgets below it hear the marker stroke, and the verdict on
   * top of it would be silent without this, in the app's voice rather than the
   * theme's without the design.
   */
  const {judge} = useFlashBookInkSound({inBook: true, design: blockDesign})

  const handleAnswer = (r: SlideAnswerResult) => {
    // The verdict sounds on *every* attempt, including a retry: the learner
    // asked a question and the book answers it. Only the report below is
    // first-attempt-only.
    judge(r.isCorrect ? 'correct' : (r.points ?? 0) > 0 ? 'partial' : 'wrong')
    // Record the first attempt only — retries inside a block don't overwrite
    // the journey report entry.
    if (lastReported.current) return
    lastReported.current = true
    onResult(r)
  }

  /**
   * The surface this block sits on. Half the theme's block gap lives here and
   * half in the divider between blocks, so a rule always lands centred in its
   * own whitespace rather than hugging the content above it.
   */
  const wrap = useMemo(() => {
    const {mode, gap, padding, radius, borderWidth} = blockDesign.block
    const rhythm = {paddingBottom: gap / 2}
    /**
     * The page's line, for a theme that prints on paper with lines on it. One
     * body line of writing is exactly one rule (see the theme design layer), so
     * a whole number of rules of leading puts the block's first line *on* a
     * line and keeps every line after it there.
     */
    const step =
      blockDesign.page.textureStep ?? blockDesign.type.body.lineHeight
    switch (mode) {
      case 'panel':
        return [
          styles.block,
          rhythm,
          {
            backgroundColor: palette.surface,
            borderWidth,
            borderColor: palette.border,
            borderRadius: radius,
            padding,
          },
        ]
      case 'inset':
        return [
          styles.block,
          rhythm,
          {
            backgroundColor: palette.surfaceAlt,
            borderRadius: radius,
            padding,
          },
        ]
      case 'ruled':
        return [
          styles.block,
          // On ruled paper the rhythm *is* the ruling: the block adds no
          // leading, no trailing and no gap of its own. A 14px inset here is
          // what put every exercise half a line below the line it was written
          // on, and the page pads each block up to the next rule between
          // blocks — so all the space a ruled page needs is already there, in
          // whole lines, and no block ever drifts off the paper's rhythm.
          {
            borderTopWidth: StyleSheet.hairlineWidth * 2,
            borderTopColor: palette.border,
            paddingTop: 0,
            paddingBottom: step > 0 ? 0 : gap / 2,
          },
        ]
      case 'open':
      default:
        return [styles.block, rhythm]
    }
  }, [
    blockDesign.block,
    blockDesign.page.textureStep,
    blockDesign.type.body.lineHeight,
    palette.border,
    palette.surface,
    palette.surfaceAlt,
  ])

  const body = (
    <FlashBookContentBlock
      block={block}
      accent={accent}
      tokens={tokens}
      design={blockDesign}
      audioLabel={blockDesign.audio.label}
      onRead={() => handleAnswer({isCorrect: true, points: 0, maxPoints: 0})}
    />
  )

  // The block's own box carries the direction, so everything the block prints
  // inherits it: rows mirror, inline text flows from the correct end, and a
  // widget that measures its own geometry (the matching strokes, the ink marks)
  // keeps working, because it measures where things *are* rather than assuming
  // which side they started on.
  const directed = useMemo(
    () =>
      [
        wrap,
        direction === 'rtl' && ({direction: 'rtl'} as const),
      ] as StyleProp<ViewStyle>,
    [wrap, direction],
  )

  // Rich reading blocks are `content` slides with a presentation, so they are
  // addressed by the key that includes it (`content:quote`) — otherwise a quote
  // and a text block would be indistinguishable to anything walking the page.
  const testKey = slideBlockKey(block)

  if (isContent || !meta || !meta.supported) {
    return (
      <View testID={`flashbook-play-block--${testKey}`} style={directed}>
        {body}
      </View>
    )
  }

  return (
    <View testID={`flashbook-play-block--${testKey}`} style={directed}>
      {/* Shared question widgets read this palette + design so a light book
          theme never ends up with dark, unreadable option cards, and answer
          rows wear the theme's shape rather than a generic bordered box. */}
      <FlashBookPlayThemeProvider
        tokens={tokens}
        design={blockDesign}
        direction={direction}>
        <SlidePlayerRenderer
          slide={block}
          primaryColor={accent}
          isPreview={isPreview}
          onAnswer={handleAnswer}
        />
      </FlashBookPlayThemeProvider>
    </View>
  )
}

const styles = StyleSheet.create({
  // No `overflow: hidden` and no fixed min-height: a themed block must never
  // clip its own controls or refuse to grow with unusually long content.
  block: {width: '100%'},
})
